import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { usePageHeader } from "../contexts/PageHeaderContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Filter,
  MapPin,
  Phone,
  Mail,
  Clock,
  CheckCircle2,
  Navigation as NavIcon,
  Play,
  Send,
  Calendar as CalIcon,
  FileText,
  Image as ImageIcon,
  Activity,
  X,
  Briefcase,
  AlertCircle,
  DollarSign,
  Crosshair,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Camera,
  StickyNote,
  Flag,
  User,
  Users,
  Building2,
  Loader2,
  Star,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { STATUS_FLOW, jobStatusColors } from "../data/jobsMock";
import { dispatchPriorityColors, getJobColor } from "../data/dispatchMock";
import { jobsApi, customersApi, techniciansApi, estimatesApi, invoicesApi, reputationApi } from "../lib/api";
import { getCurrentPositionOnce } from "../lib/gps";
import { useAuth } from "../contexts/AuthContext";
import CustomerProfileOverlay from "../components/CustomerProfileOverlay";
import RescheduleTextPopup from "../components/RescheduleTextPopup";
import AssignTechControl from "../components/AssignTechControl";
import OpenJobAlertModal from "../components/OpenJobAlertModal";
import { DocDetail } from "./Estimates";
import WorkOrderComplete from "../components/mobile/WorkOrderComplete";

// On the Jobs page, the "Unassigned" status is shown to users as "Unscheduled".
// The underlying job.status value stays "Unassigned" (Dispatch board keeps that label).
const jobStatusLabel = (s) => (s === "Unassigned" ? "Unscheduled" : s);

// ---- Work Description auto-summary -------------------------------------------
// We pre-fill the (still editable) Work Description with a full work-order summary
// assembled from the job + its linked estimate/invoice. The marker lets us detect
// an already-composed/edited description so we don't recompose over the tech's edits.
const WO_MARKER = "— WORK ORDER —";

const _money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const _fmtDate = (iso, withTime = false) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", withTime
    ? { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }
    : { month: "short", day: "numeric", year: "numeric" });
};

function composeWorkDescription(job, estimate, invoice) {
  const L = [];
  L.push(WO_MARKER);
  const svc = [job.trade, job.type].filter(Boolean).join(" · ");
  L.push(`SERVICE: ${svc || "—"}${job.priority ? `  (${job.priority} priority)` : ""}`);
  if (job.value) L.push(`JOB VALUE: ${_money(job.value)}`);
  L.push("");
  const src = invoice || estimate;
  const items = ((src && src.lineItems) || []).filter((li) => !li.isMembershipFee);
  L.push("MATERIALS / LINE ITEMS:");
  if (items.length) {
    items.forEach((li) => {
      const qty = Number(li.qty) || 1;
      L.push(`  • ${qty}× ${li.description || "Item"} — ${_money(qty * (Number(li.unitPrice) || 0))}`);
    });
  } else {
    L.push("  None yet — no linked estimate/invoice.");
  }
  L.push("");
  L.push(estimate ? `ESTIMATE: ${estimate.id} · ${_money(estimate.total)} · ${estimate.status}` : "ESTIMATE: None");
  L.push(invoice
    ? `INVOICE: ${invoice.id} · ${_money(invoice.total)} · ${invoice.status} · Balance due ${_money(invoice.balanceDue)}`
    : "INVOICE: Not invoiced yet.");
  L.push("");
  const created = (job.log || []).find((l) => l.type === "created");
  L.push(`CREATED: by ${created?.by || job.createdBy || "—"} on ${_fmtDate(created?.time || job.createdAt) || "—"}`);
  L.push(`ASSIGNED TO: ${job.tech || "Unassigned"}`);
  L.push(`SCHEDULED: ${_fmtDate(job.start, true) || "Unscheduled"}`);
  L.push("");
  L.push("NOTES:");
  L.push((job.description || "").trim() || "—");
  return L.join("\n");
}



function pinIcon(color, label, ring = "#ffffff") {
  return L.divIcon({
    html: `<div style="position:relative"><div style="background:${color};color:#fff;font-weight:700;font-size:10px;width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:3px solid ${ring};box-shadow:0 2px 6px rgba(0,0,0,.3)"><span style="transform:rotate(45deg)">${label}</span></div></div>`,
    className: "",
    iconSize: [28, 34],
    iconAnchor: [14, 30],
  });
}

function MapFocus({ pos, zoom = 16 }) {
  const map = useMap();
  useEffect(() => {
    if (pos) map.flyTo(pos, zoom, { duration: 0.6 });
  }, [pos, zoom, map]);
  return null;
}

// Haversine — meters
function distM(a, b) {
  if (!a || !b) return 0;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(x)));
}

function formatM(m) {
  if (m < 1000) return `${m} m`;
  return `${(m / 1000).toFixed(2)} km`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function Jobs() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [profileCustomerId, setProfileCustomerId] = useState(null);
  const [techs, setTechs] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [techFilter, setTechFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState(""); // YYYY-MM-DD — inclusive
  const [dateTo, setDateTo] = useState("");     // YYYY-MM-DD — inclusive
  const [sortKey, setSortKey] = useState("scheduled"); // scheduled | type | status | priority
  const [sortDir, setSortDir] = useState("asc"); // asc | desc
  const [selectedId, setSelectedId] = useState(null);
  // Estimate detail drawer rendered as an overlay on top of the job drawer.
  const [overlayEstimate, setOverlayEstimate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadAll = () =>
      Promise.all([jobsApi.list(), customersApi.list(), techniciansApi.list(), estimatesApi.list(), invoicesApi.list()])
        .then(([j, c, t, e, inv]) => {
          if (cancelled) return;
          setJobs(j);
          setCustomers(c);
          setTechs(t);
          setEstimates(e);
          setInvoices(inv);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Failed to load jobs data", err);
          toast.error("Failed to load jobs. Check backend connection.");
          setLoading(false);
        });
    loadAll();
    const onJobCreated = () => loadAll();
    window.addEventListener("job-created", onJobCreated);
    return () => {
      cancelled = true;
      window.removeEventListener("job-created", onJobCreated);
    };
  }, []);

  // Deep-link support: `/jobs?id=J-XXXX` opens that job's drawer once the list
  // is loaded. Clearing the param when the drawer closes keeps the URL clean.
  useEffect(() => {
    const id = searchParams.get("id");
    if (id && !loading && jobs.some((j) => j.id === id)) {
      setSelectedId(id);
    }
  }, [searchParams, loading, jobs]);

  // On narrow viewports, open the job in the dedicated mobile job view instead
  // of the desktop drawer (applies to every role).
  useEffect(() => {
    if (selectedId && typeof window !== "undefined" && window.innerWidth < 768) {
      navigate(`/mobile/jobs/${selectedId}`, { replace: true });
    }
  }, [selectedId, navigate]);

  const techOptions = useMemo(() => ["All", ...Array.from(new Set(techs.map((t) => t.name))), "Unassigned"], [techs]);
  // "Unassigned" here means no technician assigned. Appended so it's visible
  // in the Status pill row without also surfacing "Canceled" (kept hidden,
  // exactly as before this fix).
  const statusOptions = ["All", ...STATUS_FLOW, "Unassigned", "Canceled"];
  const statusPillOptions = ["All", ...STATUS_FLOW, "Unassigned"];
  const isUnassignedTech = (j) => !j.tech || j.tech === "Unassigned";

  // Semantic orderings for the sortable columns ("in order", not alphabetical).
  const PRIORITY_ORDER = { Urgent: 0, High: 1, Normal: 2, Low: 3 };
  const STATUS_ORDER = {
    Unassigned: 0, Scheduled: 1, Dispatched: 2, "En Route": 3,
    "On Site": 4, "In Progress": 5, Completed: 6, Canceled: 7,
  };

  // Click a header: same key flips direction; a new key starts ascending.
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const compareJobs = (a, b) => {
    let r = 0;
    if (sortKey === "scheduled") {
      r = (a.start || "").localeCompare(b.start || "");
    } else if (sortKey === "type") {
      r = (a.type || "").localeCompare(b.type || "");
    } else if (sortKey === "priority") {
      r = (PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99);
    } else if (sortKey === "status") {
      r = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
    }
    if (r === 0) r = a.id.localeCompare(b.id); // stable tiebreaker
    return sortDir === "asc" ? r : -r;
  };

  const filtered = useMemo(() => {
    // Jobs page shows only active (not-Completed) jobs — completed jobs still
    // exist and remain visible on the Job History page.
    let list = jobs.filter((j) => j.status !== "Completed");
    if (statusFilter !== "All") {
      list = statusFilter === "Unassigned"
        ? list.filter(isUnassignedTech)
        : list.filter((j) => j.status === statusFilter);
    }
    if (techFilter !== "All") list = list.filter((j) => (j.tech || "Unassigned") === techFilter);
    if (priorityFilter !== "All") list = list.filter((j) => j.priority === priorityFilter);
    // Date range filter — compares against the job's scheduled `start` (local
    // calendar day). Jobs without a start are hidden while a date filter is
    // active since they have no day to match against.
    if (dateFrom || dateTo) {
      list = list.filter((j) => {
        if (!j.start) return false;
        const d = new Date(j.start);
        if (Number.isNaN(d.getTime())) return false;
        const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        if (dateFrom && ymd < dateFrom) return false;
        if (dateTo && ymd > dateTo) return false;
        return true;
      });
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (j) =>
          j.id.toLowerCase().includes(q) ||
          j.customer.toLowerCase().includes(q) ||
          j.address.toLowerCase().includes(q) ||
          j.type.toLowerCase().includes(q),
      );
    }
    return [...list].sort(compareJobs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, search, statusFilter, techFilter, priorityFilter, dateFrom, dateTo, sortKey, sortDir]);

  const stats = useMemo(() => {
    return {
      total: jobs.length,
      active: jobs.filter((j) => ["Dispatched", "En Route", "On Site", "In Progress"].includes(j.status)).length,
      completed: jobs.filter((j) => j.status === "Completed").length,
      unassigned: jobs.filter((j) => j.status === "Unassigned").length,
    };
  }, [jobs]);

  const updateJob = (updated) => {
    setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
  };

  const selected = jobs.find((j) => j.id === selectedId);

  usePageHeader({ title: "Jobs", subtitle: `${jobs.length} jobs · ${stats.active} active · ${stats.completed} completed today` });

  return (
    <>
      <div className="p-6 space-y-4">
        {/* KPIs */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          <Kpi icon={Briefcase} label="Total Jobs" value={stats.total} accent="#2563EB" />
          <Kpi icon={Activity} label="Active Now" value={stats.active} accent="#10B981" />
          <Kpi icon={CheckCircle2} label="Completed Today" value={stats.completed} accent="#8B5CF6" />
          <Kpi icon={AlertCircle} label="Unscheduled" value={stats.unassigned} accent="#F59E0B" />
        </div>

        {/* Filter row */}
        <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap" data-testid="jobs-filter-bar">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="jobs-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by job #, customer, address, type..."
              className="w-full h-9 pl-9 pr-3 rounded-md border border-slate-200 bg-slate-50 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 focus:bg-white"
            />
          </div>
          <PillRow label="Status" options={statusPillOptions} value={statusFilter} onChange={setStatusFilter} testId="jobs-status-filter" />
          <PillRow label="Priority" options={["All", "High", "Normal", "Low"]} value={priorityFilter} onChange={setPriorityFilter} testId="jobs-priority-filter" />
          <select
            data-testid="jobs-tech-filter"
            value={techFilter}
            onChange={(e) => setTechFilter(e.target.value)}
            className="h-9 px-2.5 rounded-md border border-slate-200 bg-slate-50 text-[12.5px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 focus:bg-white"
          >
            {techOptions.map((t) => (
              <option key={t} value={t}>
                {t === "All" ? "All Technicians" : t}
              </option>
            ))}
          </select>
          {/* Date range — filters by the job's scheduled start day. Either bound
              is optional; both together give a closed inclusive window. */}
          <div className="flex items-center gap-1.5 h-9 px-2 rounded-md border border-slate-200 bg-slate-50 text-[12px] text-slate-600" data-testid="jobs-date-filter">
            <CalIcon className="h-3.5 w-3.5 text-slate-400" />
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">From</span>
            <input
              type="date"
              data-testid="jobs-date-from"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-6 px-1.5 rounded border border-slate-200 bg-white text-[12px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500/30"
            />
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">To</span>
            <input
              type="date"
              data-testid="jobs-date-to"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-6 px-1.5 rounded border border-slate-200 bg-white text-[12px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500/30"
            />
            {(dateFrom || dateTo) && (
              <button
                type="button"
                onClick={() => { setDateFrom(""); setDateTo(""); }}
                data-testid="jobs-date-clear"
                className="h-6 px-1.5 rounded text-[11px] font-semibold text-slate-500 hover:text-slate-700 hover:bg-slate-200"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Jobs table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="jobs-list">
          <div className="grid grid-cols-[110px_minmax(180px,1fr)_minmax(200px,1.4fr)_140px_140px_120px_120px_140px_36px] gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">
            <div>Job #</div>
            <div>Customer</div>
            <div>Address</div>
            <div />
            <SortHeader label="Type" col="type" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortHeader label="Status" col="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortHeader label="Priority" col="priority" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortHeader label="Scheduled" col="scheduled" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <div />
          </div>
          {loading ? (
            <div className="px-4 py-16 text-center text-[13px] text-slate-400" data-testid="jobs-loading">Loading jobs from API…</div>
          ) : (
            <>
              {filtered.map((j) => (
                <JobRow key={j.id} job={j} onClick={() => setSelectedId(j.id)} />
              ))}
              {filtered.length === 0 && (
                <div className="px-4 py-16 text-center text-[13px] text-slate-400">No jobs match these filters.</div>
              )}
            </>
          )}
        </div>
      </div>

      {selected && (
        <JobDetail
          job={selected}
          customers={customers}
          techs={techs}
          estimates={estimates}
          invoices={invoices}
          onClose={() => {
            setSelectedId(null);
            if (searchParams.get("id")) {
              const next = new URLSearchParams(searchParams);
              next.delete("id");
              setSearchParams(next, { replace: true });
            }
          }}
          onUpdate={updateJob}
          onCreateEstimate={async () => {
            try {
              // Resolve customer: prefer job.customerId, then name match, else auto-create.
              let customerId = selected.customerId;
              if (!customerId) {
                const found = customers.find((c) => c.name === selected.customer);
                if (found) customerId = found.id;
              }
              if (!customerId) {
                const newCust = await customersApi.create({
                  name: selected.customer,
                  type: "Residential",
                  phone: selected.phone || "",
                  address: selected.address || "",
                  city: selected.city || "",
                  state: selected.state || "",
                  zip: selected.zip || "",
                });
                customerId = newCust.id;
                setCustomers((prev) => [newCust, ...prev]);
                // Also link the job back to this customer for future calls
                try {
                  await jobsApi.patch(selected.id, { customerId });
                } catch (_) { /* non-blocking */ }
                toast.info(`Customer "${newCust.name}" auto-created`);
              }
              const created = await estimatesApi.create({
                customerId,
                jobId: selected.id,
                title: `${selected.type} — ${selected.customer}`,
                lineItems: [],
              });
              setEstimates((prev) => [created, ...prev]);
              setOverlayEstimate(created);
              toast.success(`Estimate ${created.id} created`);
            } catch (err) {
              console.error(err);
              toast.error(err?.response?.data?.detail || "Failed to create estimate");
            }
          }}
          onOpenEstimate={(est) => setOverlayEstimate(est)}
          onOpenCustomer={(customerId) => setProfileCustomerId(customerId)}
        />
      )}
      {overlayEstimate && (
        <DocDetail
          kind="estimate"
          doc={overlayEstimate}
          customers={customers}
          jobs={jobs}
          onClose={() => setOverlayEstimate(null)}
          onUpdated={(updated) => {
            setEstimates((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
            setOverlayEstimate(updated);
          }}
          onDeleted={(id) => {
            setEstimates((prev) => prev.filter((e) => e.id !== id));
            setOverlayEstimate(null);
          }}
          onConverted={(newInvoice) => {
            // Refresh the estimate (status flips to Converted) and switch overlay to the new invoice
            estimatesApi.get(overlayEstimate.id).then((e) => {
              setEstimates((prev) => prev.map((x) => (x.id === e.id ? e : x)));
            });
            toast.success(`Invoice ${newInvoice.id} created — open Estimates & Invoices to record payments`);
            setOverlayEstimate(null);
          }}
        />
      )}
      <CustomerProfileOverlay customerId={profileCustomerId} onClose={() => setProfileCustomerId(null)} />
    </>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-all">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">{label}</div>
          <div className="mt-1.5 text-[24px] font-bold text-slate-900 leading-none">{value}</div>
        </div>
        <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: `${accent}18` }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
      </div>
    </div>
  );
}

function PillRow({ label, options, value, onChange, testId }) {
  return (
    <div className="flex items-center gap-1 bg-slate-50 rounded-lg p-1 border border-slate-200" data-testid={testId}>
      <span className="text-[10.5px] uppercase font-semibold text-slate-400 tracking-wider px-1.5">{label}</span>
      {options.map((o) => (
        <button
          key={o}
          onClick={() => onChange(o)}
          className={`h-7 px-2.5 rounded-md text-[11.5px] font-medium transition-colors ${
            value === o ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

function SortHeader({ label, col, sortKey, sortDir, onSort }) {
  const active = sortKey === col;
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      data-testid={`jobs-sort-${col}`}
      aria-label={`Sort by ${label}${active ? (sortDir === "asc" ? ", ascending" : ", descending") : ""}`}
      className={`inline-flex items-center gap-1 uppercase tracking-wider font-semibold transition-colors hover:text-slate-800 ${
        active ? "text-blue-600" : "text-slate-500"
      }`}
    >
      {label}
      {active ? (
        sortDir === "asc" ? (
          <ChevronUp className="h-3 w-3" data-testid={`jobs-sort-${col}-asc`} />
        ) : (
          <ChevronDown className="h-3 w-3" data-testid={`jobs-sort-${col}-desc`} />
        )
      ) : (
        <ChevronsUpDown className="h-3 w-3 text-slate-300" />
      )}
    </button>
  );
}

function JobRow({ job, onClick }) {
  const sc = jobStatusColors[job.status] || jobStatusColors.Scheduled;
  const pc = dispatchPriorityColors[job.priority] || dispatchPriorityColors.Normal;
  const tc = getJobColor(job.type);
  // A tech is actively working the job the moment they mark En Route (or later),
  // until it's Completed or Canceled. Shown as a green "In Progress" tag between
  // the address and the type column so dispatchers can spot live jobs at a glance.
  const isActivelyWorked = ["En Route", "On Site", "In Progress"].includes(job.status);
  return (
    <button
      data-testid={`jobs-row-${job.id}`}
      onClick={onClick}
      className="w-full text-left grid grid-cols-[110px_minmax(180px,1fr)_minmax(200px,1.4fr)_140px_140px_120px_120px_140px_36px] gap-3 px-4 py-3 border-b border-slate-100 hover:bg-blue-50/40 transition-colors items-center"
    >
      <div className="text-[12.5px] font-mono font-semibold text-slate-700">{job.id}</div>
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-slate-900 truncate">{job.customer}</div>
        <div className="text-[11px] text-slate-500 truncate">{job.tech || "Unassigned"}</div>
      </div>
      <div className="min-w-0">
        <div className="text-[12.5px] text-slate-700 truncate">{job.address}</div>
        <div className="text-[11px] text-slate-500 truncate">{job.city}, {job.state} {job.zip}</div>
      </div>
      <div>
        {isActivelyWorked && (
          <span
            data-testid={`jobs-row-in-progress-${job.id}`}
            className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-500"
            title="A technician is currently working this job"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </span>
            In Progress
          </span>
        )}
      </div>
      <div>
        <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded" style={{ background: tc.bg, color: tc.text }}>
          {job.type}
        </span>
      </div>
      <div>
        <span className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.text }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: sc.dot }} />
          {jobStatusLabel(job.status)}
        </span>
      </div>
      <div>
        <span className="text-[10.5px] font-bold px-2 py-0.5 rounded" style={{ background: pc.bg, color: pc.text }}>
          {job.priority}
        </span>
      </div>
      <div className="text-[11.5px] text-slate-600">
        {job.start
          ? new Date(job.start).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
          : "—"}
      </div>
      <ChevronRight className="h-4 w-4 text-slate-400" />
    </button>
  );
}

function iconForLog(type) {
  switch (type) {
    case "created": return <Plus className="h-3.5 w-3.5" />;
    case "scheduled": return <CalIcon className="h-3.5 w-3.5" />;
    case "dispatched": return <Send className="h-3.5 w-3.5" />;
    case "enroute": return <NavIcon className="h-3.5 w-3.5" />;
    case "arrived": return <MapPin className="h-3.5 w-3.5" />;
    case "started": return <Play className="h-3.5 w-3.5" />;
    case "finished": return <CheckCircle2 className="h-3.5 w-3.5" />;
    case "invoiced": return <FileText className="h-3.5 w-3.5" />;
    case "note": return <StickyNote className="h-3.5 w-3.5" />;
    case "photo": return <Camera className="h-3.5 w-3.5" />;
    case "status": return <Flag className="h-3.5 w-3.5" />;
    case "assigned": return <User className="h-3.5 w-3.5" />;
    default: return <Activity className="h-3.5 w-3.5" />;
  }
}

const logColor = {
  created: "#64748B",
  scheduled: "#2563EB",
  dispatched: "#F59E0B",
  enroute: "#F97316",
  arrived: "#10B981",
  started: "#10B981",
  finished: "#059669",
  invoiced: "#8B5CF6",
  note: "#0EA5E9",
  photo: "#EC4899",
  status: "#475569",
  assigned: "#6366F1",
};

export function JobDetail({ job, customers, techs = [], estimates = [], invoices = [], onClose, onUpdate, onCreateEstimate, onOpenEstimate, onOpenCustomer }) {
  const { hasModule, user } = useAuth();
  const canAssign = user?.role === "admin" || user?.role === "dispatcher";
  const [tab, setTab] = useState("overview");
  const [focusPos, setFocusPos] = useState(null);
  const [capturing, setCapturing] = useState(null); // "arrival" | "finish" | null
  const [noteDraft, setNoteDraft] = useState("");
  const [creatingEstimate, setCreatingEstimate] = useState(false);
  const [sendingReview, setSendingReview] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [busyMyPart, setBusyMyPart] = useState(false);
  const [openJobAlert, setOpenJobAlert] = useState(null); // { jobId }
  const [rescheduleJob, setRescheduleJob] = useState(null);
  const fileInputRef = useRef(null);

  const linkedEstimates = useMemo(
    () => (estimates || []).filter((e) => e.jobId === job.id),
    [estimates, job.id],
  );

  // Linked estimate (newest) + its invoice (by jobId, estimateId, or the estimate's
  // convertedInvoiceId) — used to assemble the Work Description summary.
  const linkedEstimate = useMemo(
    () => linkedEstimates.slice().sort((a, b) => (b.issuedAt || "").localeCompare(a.issuedAt || ""))[0] || null,
    [linkedEstimates],
  );
  const linkedInvoice = useMemo(() => {
    const ids = new Set([job.estimateId, linkedEstimate?.id].filter(Boolean));
    const convId = linkedEstimate?.convertedInvoiceId;
    return (invoices || []).find(
      (v) => v.jobId === job.id || ids.has(v.estimateId) || (convId && v.id === convId),
    ) || null;
  }, [invoices, job.id, job.estimateId, linkedEstimate]);

  // Pre-fill the editable Work Description with the full work-order summary, unless
  // it was already composed/edited (detected via the WO_MARKER) — then keep as-is.
  const initialDescription = useMemo(() => {
    if ((job.description || "").includes(WO_MARKER)) return job.description;
    return composeWorkDescription(job, linkedEstimate, linkedInvoice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id, linkedEstimate, linkedInvoice]);

  const [descDraft, setDescDraft] = useState(initialDescription);
  // Reset the editable draft only when switching to a different job (avoids
  // clobbering a tech's unsaved edits when linked docs refresh in the background).
  useEffect(() => {
    setDescDraft(initialDescription);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  // Job Summary — separate from Work Description, stored on job.guruSummary
  // and kept in sync with any linked estimate's Job Summary block.
  const [summaryDraft, setSummaryDraft] = useState(job.guruSummary || "");
  useEffect(() => {
    setSummaryDraft(job.guruSummary || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  const customer = (customers || []).find((c) => c.name === job.customer);
  const sc = jobStatusColors[job.status] || jobStatusColors.Scheduled;
  const pc = dispatchPriorityColors[job.priority] || dispatchPriorityColors.Normal;
  const tc = getJobColor(job.type);

  const site = job.coords || { lat: 33.4484, lng: -112.074 };
  const arrival = job.log.find((l) => l.type === "arrived")?.location;
  const finish = job.log.find((l) => l.type === "finished")?.location;
  const enroute = job.log.find((l) => l.type === "enroute")?.location;

  const arrivalDistance = arrival ? distM(arrival, site) : null;
  const finishDistance = finish ? distM(finish, site) : null;

  const advanceTo = async (nextStatus, extra = {}) => {
    try {
      const gpsEvents = new Set(["En Route", "On Site", "In Progress", "Completed"]);
      const gps = gpsEvents.has(nextStatus) ? await getCurrentPositionOnce() : null;
      const updated = await jobsApi.updateStatus(job.id, nextStatus, gps, extra);
      onUpdate(updated);
      toast.success(`Status updated to ${nextStatus}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "open_job") {
        setOpenJobAlert({ jobId: detail.jobId });
        return;
      }
      console.error(err);
      toast.error("Failed to update status");
    }
  };

  // "Complete my part" — a tech on a multi-tech job releases themselves. If
  // they are the LAST active assignee we walk them through the full close-out
  // wizard (checklist/parts/payment/sign-off/disposition), same as mobile.
  const handleCompleteClick = async () => {
    const crew = (job?.assignments || []).length;
    if (crew <= 1) {
      setShowCompletion(true);
      return;
    }
    setBusyMyPart(true);
    try {
      const res = await jobsApi.completeMyPart(job.id);
      onUpdate(res.job);
      if (res.isLast) {
        toast.success("You're the last one on this job — let's close it out");
        setShowCompletion(true);
      } else {
        toast.success(`Your part is done — ${res.remaining} teammate${res.remaining === 1 ? "" : "s"} still working.`);
        onClose();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update your assignment");
    } finally {
      setBusyMyPart(false);
    }
  };

  const sendReviewRequest = async (force = false) => {
    setSendingReview(true);
    try {
      const result = await reputationApi.sendReviewRequest(job.id, force);
      if (result.reason === "already_sent" && !force) {
        toast(`A review request was already sent on ${new Date(result.sentAt).toLocaleDateString()}`, {
          action: { label: "Send anyway", onClick: () => sendReviewRequest(true) },
        });
      } else if (result.reason === "no_customer_email") {
        toast.error("This customer has no email on file");
      } else if (result.sent) {
        toast.success("Review request sent to the customer");
      } else {
        toast.error("Could not send the review request");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the review request");
    } finally {
      setSendingReview(false);
    }
  };

  // Inline patch used by the header schedule/tech editors. Silent on success,
  // surfaces errors so the dispatcher knows what didn't save.
  const patchJob = async (patch, successMsg) => {
    try {
      const updated = await jobsApi.patch(job.id, patch);
      onUpdate(updated);
      if (successMsg) toast.success(successMsg);
      return updated;
    } catch (err) {
      console.error(err);
      toast.error("Failed to save changes");
      return null;
    }
  };

  const captureLocation = (kind) => {
    setCapturing(kind);
    const post = async (lat, lng, source) => {
      try {
        const updated = await jobsApi.geoCapture(job.id, { kind, lat, lng, source });
        onUpdate(updated);
        const d = distM({ lat, lng }, site);
        if (kind === "arrival") toast.success(`Arrival captured · ${formatM(d)} from job site`);
        else toast.success("Job marked complete · finish time logged");
        setFocusPos([lat, lng]);
      } catch (err) {
        console.error(err);
        toast.error("Failed to save capture");
      } finally {
        setCapturing(null);
      }
    };

    if (!navigator.geolocation) {
      const jitter = () => (Math.random() - 0.5) * 0.0006;
      post(site.lat + jitter(), site.lng + jitter(), "mock");
      return;
    }
    const timer = setTimeout(() => {
      const jitter = () => (Math.random() - 0.5) * 0.0006;
      post(site.lat + jitter(), site.lng + jitter(), "mock-timeout");
    }, 6000);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timer);
        post(pos.coords.latitude, pos.coords.longitude, "gps");
      },
      () => {
        clearTimeout(timer);
        const jitter = () => (Math.random() - 0.5) * 0.0006;
        post(site.lat + jitter(), site.lng + jitter(), "mock-denied");
        toast.info("Location permission unavailable — used mock coords");
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 },
    );
  };

  const addNote = async () => {
    if (!noteDraft.trim()) return;
    try {
      const updated = await jobsApi.addNote(job.id, noteDraft.trim());
      onUpdate(updated);
      setNoteDraft("");
      toast.success("Note added to job timeline");
    } catch (err) {
      console.error(err);
      toast.error("Failed to add note");
    }
  };

  const saveDescription = async () => {
    try {
      const updated = await jobsApi.patch(job.id, { description: descDraft });
      onUpdate(updated);
      toast.success("Job description updated");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save description");
    }
  };

  const saveSummary = async () => {
    try {
      const updated = await jobsApi.updateSummary(job.id, summaryDraft);
      onUpdate(updated);
      toast.success("Job Summary updated");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save Job Summary");
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    Promise.all(
      files.map(
        (f) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (ev) => resolve({ id: `ph-${Date.now()}-${Math.floor(Math.random() * 1000)}`, name: f.name, url: ev.target.result, caption: "Uploaded from field" });
            reader.readAsDataURL(f);
          }),
      ),
    ).then(async (newPhotos) => {
      try {
        const updated = await jobsApi.uploadPhotos(job.id, newPhotos);
        onUpdate(updated);
        toast.success(`${newPhotos.length} photo(s) added`);
      } catch (err) {
        console.error(err);
        toast.error("Failed to upload photos");
      }
    });
    e.target.value = "";
  };

  const currentIdx = STATUS_FLOW.indexOf(job.status);
  const nextStatus = currentIdx >= 0 && currentIdx < STATUS_FLOW.length - 1 ? STATUS_FLOW[currentIdx + 1] : null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/40" />
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid={`job-detail-${job.id}`}
        className="relative w-[760px] max-w-[95vw] h-full bg-white shadow-2xl border-l border-slate-200 overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[11px] font-mono font-semibold text-slate-500">
                <span>{job.id}</span>
                <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded" style={{ background: tc.bg, color: tc.text }}>
                  {job.type}
                </span>
              </div>
              <h2 className="mt-0.5 text-[18px] font-bold text-slate-900 truncate">
                {onOpenCustomer && (job.customerId || customer?.id) ? (
                  <button
                    type="button"
                    onClick={() => onOpenCustomer(job.customerId || customer.id)}
                    data-testid="job-detail-customer-link"
                    title={`Open ${job.customer}'s profile`}
                    className="text-slate-900 hover:text-blue-700 hover:underline underline-offset-2 decoration-2 decoration-blue-300 transition-colors text-left"
                  >
                    {job.customer}
                  </button>
                ) : (
                  job.customer
                )}
              </h2>
              <div className="text-[12.5px] text-slate-500 truncate flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {job.address}, {job.city}, {job.state} {job.zip}
              </div>
              <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                <span className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.text }}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: sc.dot }} />
                  {jobStatusLabel(job.status)}
                </span>
                <label
                  className="inline-flex items-center text-[10.5px] font-bold px-1.5 py-0.5 rounded cursor-pointer"
                  style={{ background: pc.bg, color: pc.text }}
                  title="Change priority"
                >
                  <select
                    data-testid="job-detail-priority-select"
                    value={job.priority || "Normal"}
                    onChange={(e) => patchJob({ priority: e.target.value }, `Priority set to ${e.target.value}`)}
                    className="bg-transparent border-0 text-[10.5px] font-bold focus:outline-none focus:ring-0 cursor-pointer appearance-none pr-0.5"
                    style={{ color: pc.text }}
                  >
                    {["High", "Normal", "Low"].map((p) => (
                      <option key={p} value={p}>{p} Priority</option>
                    ))}
                  </select>
                  <ChevronDown className="h-3 w-3 opacity-70" />
                </label>
                <AssignTechControl
                  job={job}
                  techs={techs}
                  canAssign={canAssign}
                  size="sm"
                  onJobUpdated={(updated) => onUpdate(updated)}
                />
                {job.value && (
                  <span className="inline-flex items-center gap-1 text-[10.5px] font-medium px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">
                    <DollarSign className="h-3 w-3" /> ${job.value.toLocaleString()}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center shrink-0" data-testid="job-detail-close">
              <X className="h-4 w-4 text-slate-500" />
            </button>
          </div>

          {/* Quick action bar */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Button
              data-testid="job-capture-arrival-btn"
              onClick={() => captureLocation("arrival")}
              disabled={!!capturing || job.status === "Completed"}
              className="h-8 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[12px] gap-1.5"
            >
              {capturing === "arrival" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crosshair className="h-3.5 w-3.5" />}
              Capture Arrival
            </Button>
            <Button
              data-testid="job-capture-finish-btn"
              onClick={() => captureLocation("finish")}
              disabled={!!capturing || job.status === "Completed"}
              className="h-8 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[12px] gap-1.5"
            >
              {capturing === "finish" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Capture Finish
            </Button>
            {nextStatus && (() => {
              const assigned = job.tech && job.tech !== "Unassigned";
              const dispatchReady = nextStatus === "Dispatched" && assigned;
              const isCompleteBtn = nextStatus === "Completed";
              const isMulti = (job.assignments || []).length > 1;
              return (
                <Button
                  data-testid="job-advance-status-btn"
                  onClick={() => (isCompleteBtn ? handleCompleteClick() : advanceTo(nextStatus))}
                  disabled={busyMyPart}
                  variant={dispatchReady ? "default" : "outline"}
                  className={`h-8 text-[12px] gap-1.5 ${
                    dispatchReady
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white border-transparent"
                      : "border-slate-300"
                  }`}
                >
                  <Flag className="h-3.5 w-3.5" /> {isCompleteBtn && isMulti ? "Complete my part" : `Mark ${nextStatus}`}
                </Button>
              );
            })()}
            <Button
              data-testid="job-create-estimate-btn"
              onClick={async () => {
                if (!onCreateEstimate || creatingEstimate) return;
                setCreatingEstimate(true);
                try {
                  await onCreateEstimate();
                } finally {
                  setCreatingEstimate(false);
                }
              }}
              disabled={creatingEstimate}
              variant="outline"
              className="h-8 text-[12px] gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              {creatingEstimate ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
              Create Estimate
            </Button>
            {job.status === "Completed" && hasModule("reputation") && (
              <Button
                data-testid="job-send-review-request-btn"
                onClick={() => sendReviewRequest(false)}
                disabled={sendingReview}
                variant="outline"
                className="h-8 text-[12px] gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                {sendingReview ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Star className="h-3.5 w-3.5" />}
                Send Review Request
              </Button>
            )}
            <div className="ml-auto flex items-center gap-1.5">
              <Clock className="h-3 w-3 text-slate-400" />
              <input
                type="datetime-local"
                value={job.start ? new Date(job.start).toISOString().slice(0, 16) : ""}
                onChange={async (e) => {
                  const v = e.target.value;
                  if (!v) {
                    patchJob({ start: null, end: null }, "Schedule cleared");
                    return;
                  }
                  const startDate = new Date(v);
                  // Preserve the current duration when nudging start; default 90 min if no prior end.
                  const priorStart = job.start ? new Date(job.start).getTime() : null;
                  const priorEnd = job.end ? new Date(job.end).getTime() : null;
                  const durationMs =
                    priorStart && priorEnd ? Math.max(15 * 60 * 1000, priorEnd - priorStart) : 90 * 60 * 1000;
                  const endDate = new Date(startDate.getTime() + durationMs);
                  const wasScheduled =
                    !!priorStart && !["Unassigned", "Completed", "Canceled"].includes(job.status);
                  const updated = await patchJob(
                    { start: startDate.toISOString(), end: endDate.toISOString() },
                    "Schedule updated",
                  );
                  if (
                    updated &&
                    wasScheduled &&
                    startDate.getTime() !== priorStart &&
                    (job.phone || job.customerId)
                  ) {
                    setRescheduleJob(updated);
                  }
                }}
                data-testid="job-detail-start-input"
                className="h-7 px-1.5 rounded border border-slate-200 bg-white text-[11px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <span className="text-[11px] text-slate-400">→</span>
              <input
                type="datetime-local"
                value={job.end ? new Date(job.end).toISOString().slice(0, 16) : ""}
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) {
                    patchJob({ end: null }, "End time cleared");
                    return;
                  }
                  patchJob({ end: new Date(v).toISOString() }, "End time updated");
                }}
                data-testid="job-detail-end-input"
                className="h-7 px-1.5 rounded border border-slate-200 bg-white text-[11px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>

          {/* Linked estimates pill row */}
          {linkedEstimates.length > 0 && (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="job-linked-estimates">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400">Linked:</span>
              {linkedEstimates.map((e) => (
                <button
                  key={e.id}
                  onClick={() => onOpenEstimate && onOpenEstimate(e)}
                  data-testid={`job-linked-estimate-${e.id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 transition-colors"
                  title={`Open ${e.id}`}
                >
                  <FileText className="h-3 w-3" />
                  {e.id}
                  <span className="text-[9.5px] font-bold uppercase tracking-wider text-blue-500">{e.status}</span>
                  {e.total > 0 && <span className="text-emerald-700">· ${e.total.toLocaleString()}</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-slate-100 flex items-center gap-0.5">
          {[
            { k: "overview", label: "Overview", icon: Activity },
            { k: "geo", label: "Geo & Timing", icon: NavIcon },
            { k: "photos", label: "Photos", icon: ImageIcon, count: (job.photos || []).length },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`job-tab-${t.k}`}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-[12px] font-medium border-b-2 transition-colors whitespace-nowrap ${
                  tab === t.k ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
                {typeof t.count === "number" && (
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tab === t.k ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {tab === "overview" && (
            <OverviewTab
              job={job}
              customer={customer}
              descDraft={descDraft}
              setDescDraft={setDescDraft}
              saveDescription={saveDescription}
              summaryDraft={summaryDraft}
              setSummaryDraft={setSummaryDraft}
              saveSummary={saveSummary}
              noteDraft={noteDraft}
              setNoteDraft={setNoteDraft}
              addNote={addNote}
              onOpenCustomer={onOpenCustomer}
              onFocusGeo={(loc) => {
                if (!loc) return;
                setTab("geo");
                setFocusPos([loc.lat, loc.lng]);
              }}
            />
          )}
          {tab === "geo" && (
            <GeoTab
              job={job}
              site={site}
              arrival={arrival}
              finish={finish}
              enroute={enroute}
              arrivalDistance={arrivalDistance}
              finishDistance={finishDistance}
              focusPos={focusPos}
              setFocusPos={setFocusPos}
            />
          )}
          {tab === "photos" && (
            <PhotosTab job={job} onUpload={() => fileInputRef.current?.click()} canUpload={hasModule("photo_signature")} />
          )}
        </div>

        <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} data-testid="job-photo-upload-input" />
      </div>

      {showCompletion && (
        <div onClick={(e) => e.stopPropagation()}>
          <WorkOrderComplete
            job={job}
            invoices={invoices}
            desktop
            onClose={() => setShowCompletion(false)}
            onCompleted={async (dispositionInfo) => {
              await advanceTo(dispositionInfo?.status || "Completed", { disposition: dispositionInfo?.name });
              setShowCompletion(false);
            }}
          />
        </div>
      )}
      {rescheduleJob && (
        <div onClick={(e) => e.stopPropagation()}>
          <RescheduleTextPopup job={rescheduleJob} onClose={() => setRescheduleJob(null)} />
        </div>
      )}
      {openJobAlert && (
        <div onClick={(e) => e.stopPropagation()}>
          <OpenJobAlertModal
            jobId={openJobAlert.jobId}
            isAdmin={user?.role === "admin"}
            onClose={() => setOpenJobAlert(null)}
            onOverride={() => {
              setOpenJobAlert(null);
              advanceTo("En Route", { override: true });
            }}
          />
        </div>
      )}
    </div>
  );
}

function OverviewTab({ job, customer, descDraft, setDescDraft, saveDescription, summaryDraft, setSummaryDraft, saveSummary, noteDraft, setNoteDraft, addNote, onOpenCustomer, onFocusGeo }) {
  return (
    <div className="space-y-4">
      {/* Status progress bar */}
      <div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Status Progression</div>
        <div className="flex items-center gap-1">
          {STATUS_FLOW.map((s, i) => {
            const curIdx = STATUS_FLOW.indexOf(job.status);
            const done = curIdx >= i;
            const isCurrent = curIdx === i;
            return (
              <React.Fragment key={s}>
                <div className="flex-1 flex flex-col items-center">
                  <div
                    className={`h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      done ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"
                    } ${isCurrent ? "ring-2 ring-emerald-300" : ""}`}
                  >
                    {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
                  </div>
                  <div className={`mt-1 text-[10px] font-semibold text-center ${done ? "text-slate-800" : "text-slate-400"}`}>{s}</div>
                </div>
                {i < STATUS_FLOW.length - 1 && (
                  <div className={`h-0.5 flex-1 ${curIdx > i ? "bg-emerald-500" : "bg-slate-200"}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Customer card */}
      {customer && (
        <div
          role={onOpenCustomer ? "button" : undefined}
          tabIndex={onOpenCustomer ? 0 : undefined}
          data-testid={onOpenCustomer ? "job-detail-customer-card" : undefined}
          onClick={(e) => {
            // Don't hijack clicks on the inner tel:/mailto: links.
            if (e.target.closest("a")) return;
            if (onOpenCustomer) onOpenCustomer(job.customerId || customer.id);
          }}
          onKeyDown={(e) => {
            if (!onOpenCustomer) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onOpenCustomer(job.customerId || customer.id);
            }
          }}
          title={onOpenCustomer ? `Open ${customer.name}'s profile` : undefined}
          className={`border border-slate-200 rounded-lg p-3 bg-slate-50/40 ${
            onOpenCustomer
              ? "cursor-pointer hover:border-blue-300 hover:bg-blue-50/40 hover:shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              : ""
          }`}
        >
          <div className="flex items-start gap-2.5">
            <div className={`h-9 w-9 rounded-lg ${customer.type === "Commercial" ? "bg-violet-100" : "bg-blue-100"} flex items-center justify-center shrink-0`}>
              {customer.type === "Commercial" ? <Building2 className="h-4 w-4 text-violet-600" /> : <User className="h-4 w-4 text-blue-600" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-slate-900">{customer.name}</div>
              <div className="text-[11px] text-slate-500">{customer.type} · Customer since {new Date(customer.since).getFullYear()}</div>
              <div className="mt-1.5 grid grid-cols-2 gap-y-0.5 text-[11.5px]">
                <a href={`tel:${customer.phone.replace(/[^0-9]/g, "")}`} className="flex items-center gap-1.5 text-blue-600 hover:underline">
                  <Phone className="h-3 w-3" /> {customer.phone}
                </a>
                <a href={`mailto:${customer.email}`} className="flex items-center gap-1.5 text-blue-600 hover:underline truncate">
                  <Mail className="h-3 w-3 shrink-0" /> <span className="truncate">{customer.email}</span>
                </a>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">LTV</div>
              <div className="text-[13px] font-bold text-emerald-700">${customer.lifetimeValue.toLocaleString()}</div>
            </div>
          </div>
        </div>
      )}

      {/* Job Summary — separate field from Work Description, syncs with any
          linked estimate's Job Summary block. */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Job Summary</div>
          {summaryDraft !== (job.guruSummary || "") && (
            <button onClick={saveSummary} className="text-[11px] font-semibold text-blue-600 hover:underline" data-testid="job-save-summary">
              Save
            </button>
          )}
        </div>
        <textarea
          data-testid="job-summary-textarea"
          value={summaryDraft}
          onChange={(e) => setSummaryDraft(e.target.value)}
          rows={4}
          placeholder="Summarize this job for the customer's estimate/invoice..."
          className="w-full px-3 py-2 rounded-md border-2 border-slate-800 text-[12.5px] leading-relaxed shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-y min-h-[80px]"
        />
      </div>

      {/* Description */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Work Description</div>
          {descDraft !== (job.description || "") && (
            <button onClick={saveDescription} className="text-[11px] font-semibold text-blue-600 hover:underline" data-testid="job-save-desc">
              Save
            </button>
          )}
        </div>
        <textarea
          data-testid="job-desc-textarea"
          value={descDraft}
          onChange={(e) => setDescDraft(e.target.value)}
          rows={16}
          placeholder="Describe the work to be performed..."
          className="w-full px-3 py-2 rounded-md border border-slate-200 text-[12.5px] leading-relaxed font-mono whitespace-pre-wrap focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 resize-y min-h-[120px]"
        />
      </div>

      {/* Quick note */}
      <div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Add Note to Timeline</div>
        <div className="flex items-center gap-2">
          <input
            data-testid="job-note-input"
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addNote()}
            placeholder="e.g. Called customer to confirm arrival window"
            className="flex-1 h-9 px-3 rounded-md border border-slate-200 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          />
          <Button
            data-testid="job-note-add-btn"
            onClick={addNote}
            disabled={!noteDraft.trim()}
            className="h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[12.5px]"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        </div>

        {/* Timeline (moved here from the removed Timeline tab) */}
        <div className="mt-4" data-testid="job-timeline-list">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Timeline</div>
          {(!job.log || job.log.length === 0) ? (
            <div className="text-[12px] text-slate-400 border border-dashed border-slate-200 rounded-md px-3 py-4 text-center">
              No timeline events yet.
            </div>
          ) : (
            <TimelineTab job={job} onFocus={onFocusGeo} />
          )}
        </div>
      </div>
    </div>
  );
}

function GeoTab({ job, site, arrival, finish, enroute, arrivalDistance, finishDistance, focusPos, setFocusPos }) {
  const center = focusPos || [site.lat, site.lng];
  const path = [];
  if (enroute) path.push([enroute.lat, enroute.lng]);
  if (arrival) path.push([arrival.lat, arrival.lng]);
  else path.push([site.lat, site.lng]);
  if (finish && finish.lat !== arrival?.lat) path.push([finish.lat, finish.lng]);

  // Duration on site
  const arrivedAt = job.log.find((l) => l.type === "arrived")?.time;
  const finishedAt = job.log.find((l) => l.type === "finished")?.time;
  let durationLabel = "—";
  if (arrivedAt && finishedAt) {
    const mins = Math.round((new Date(finishedAt).getTime() - new Date(arrivedAt).getTime()) / 60000);
    if (mins >= 60) durationLabel = `${Math.floor(mins / 60)}h ${mins % 60}m`;
    else durationLabel = `${mins} min`;
  } else if (arrivedAt && !finishedAt) {
    durationLabel = "In progress";
  }

  return (
    <div className="space-y-3">
      {/* Map */}
      <div className="rounded-md overflow-hidden border border-slate-200" style={{ height: 320 }}>
        <MapContainer center={center} zoom={15} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapFocus pos={focusPos} />
          {/* Job site */}
          <Marker position={[site.lat, site.lng]} icon={pinIcon("#2563EB", "📍")}>
            <Popup>
              <strong>Job site</strong>
              <br />
              {job.address}
            </Popup>
          </Marker>
          <CircleMarker center={[site.lat, site.lng]} radius={60} pathOptions={{ color: "#2563EB", fillColor: "#2563EB", fillOpacity: 0.06, weight: 1, dashArray: "4 4" }} />
          {arrival && (
            <Marker position={[arrival.lat, arrival.lng]} icon={pinIcon("#10B981", "A")}>
              <Popup>
                <strong>Arrival</strong>
                <br />
                {fmtTime(job.log.find((l) => l.type === "arrived")?.time)}
                <br />
                {formatM(distM(arrival, site))} from site
              </Popup>
            </Marker>
          )}
          {finish && (
            <Marker position={[finish.lat, finish.lng]} icon={pinIcon("#059669", "F")}>
              <Popup>
                <strong>Finish</strong>
                <br />
                {fmtTime(job.log.find((l) => l.type === "finished")?.time)}
                <br />
                {formatM(distM(finish, site))} from site
              </Popup>
            </Marker>
          )}
          {enroute && (
            <Marker position={[enroute.lat, enroute.lng]} icon={pinIcon("#F97316", "E")}>
              <Popup>En route waypoint</Popup>
            </Marker>
          )}
          {path.length > 1 && (
            <Polyline positions={path} pathOptions={{ color: "#10B981", weight: 3, dashArray: "6 6", opacity: 0.7 }} />
          )}
        </MapContainer>
      </div>

      {/* Distance/time cards */}
      <div className="grid grid-cols-3 gap-3">
        <GeoCard
          label="Arrival"
          accent="#10B981"
          time={arrivedAt}
          empty="Not captured"
          extra={arrivalDistance !== null ? `${formatM(arrivalDistance)} from site` : null}
        />
        <GeoCard
          label="Finish"
          accent="#059669"
          time={finishedAt}
          empty="In progress"
          extra={finishDistance !== null ? `${formatM(finishDistance)} from site` : null}
        />
        <GeoCard label="On-site Duration" accent="#2563EB" valueOverride={durationLabel} />
      </div>

      {/* Coords readout */}
      <div className="border border-slate-200 rounded-md p-3 text-[11.5px] font-mono space-y-1 bg-slate-50/40">
        <div className="flex items-center justify-between">
          <span className="text-slate-500">Job site</span>
          <span className="text-slate-700">{site.lat.toFixed(5)}, {site.lng.toFixed(5)}</span>
        </div>
        {arrival && (
          <div className="flex items-center justify-between">
            <span className="text-emerald-600">Arrival</span>
            <span className="text-slate-700">{arrival.lat.toFixed(5)}, {arrival.lng.toFixed(5)}</span>
          </div>
        )}
        {finish && (
          <div className="flex items-center justify-between">
            <span className="text-emerald-700">Finish</span>
            <span className="text-slate-700">{finish.lat.toFixed(5)}, {finish.lng.toFixed(5)}</span>
          </div>
        )}
      </div>

      <div className="text-[11px] text-slate-500 flex items-start gap-1.5">
        <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-400" />
        Locations use the browser's HTML5 Geolocation API. If permission is denied or unavailable, coords near the job site are recorded as a mock.
      </div>
    </div>
  );
}

function GeoCard({ label, accent, time, empty, extra, valueOverride }) {
  return (
    <div className="border border-slate-200 rounded-md p-3">
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold" style={{ color: accent }}>{label}</div>
        <Clock className="h-3 w-3 text-slate-400" />
      </div>
      <div className="mt-0.5 text-[14px] font-bold text-slate-900">
        {valueOverride || (time ? fmtTime(time) : empty)}
      </div>
      {extra && <div className="text-[10.5px] text-slate-500 mt-0.5">{extra}</div>}
    </div>
  );
}

function TimelineTab({ job, onFocus }) {
  // Newest → oldest so the most recent status change is at the top.
  const sorted = [...job.log].sort((a, b) => (b.time || "").localeCompare(a.time || ""));
  return (
    <div className="relative pl-6">
      <div className="absolute left-2.5 top-1 bottom-1 w-px bg-slate-200" />
      <div className="space-y-3">
        {sorted.map((entry) => {
          const color = logColor[entry.type] || "#64748B";
          const clickable = !!entry.location;
          return (
            <div key={entry.id} className="relative">
              <div
                className="absolute -left-[18px] top-1 h-5 w-5 rounded-full flex items-center justify-center text-white shadow"
                style={{ background: color }}
              >
                {iconForLog(entry.type)}
              </div>
              <button
                onClick={() => clickable && onFocus(entry.location)}
                disabled={!clickable}
                className={`w-full text-left border border-slate-200 rounded-md px-3 py-2 ${
                  clickable ? "hover:border-blue-300 hover:bg-blue-50/40 cursor-pointer" : "cursor-default"
                } transition-all`}
                data-testid={`job-timeline-entry-${entry.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[12.5px] font-semibold text-slate-900">{entry.label}</div>
                  <div className="text-[10.5px] text-slate-500 shrink-0">{fmtTime(entry.time)}</div>
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-500">
                  {entry.by && <span>by {entry.by}</span>}
                  {entry.location && (
                    <span className="flex items-center gap-1 text-blue-600">
                      <MapPin className="h-3 w-3" /> {entry.location.label || `${entry.location.lat.toFixed(4)}, ${entry.location.lng.toFixed(4)}`}
                    </span>
                  )}
                  {entry.source && entry.source !== "gps" && (
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-600">mock</span>
                  )}
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PhotosTab({ job, onUpload, canUpload }) {
  const photos = job.photos || [];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-slate-600">
          {photos.length} {photos.length === 1 ? "photo" : "photos"} attached to this job
        </div>
        {canUpload && (
        <Button onClick={onUpload} className="h-8 bg-blue-600 hover:bg-blue-700 text-white text-[12px] gap-1.5" data-testid="job-photo-upload-btn">
          <Camera className="h-3.5 w-3.5" /> Upload Photos
        </Button>
        )}
      </div>
      {photos.length === 0 ? (
        <div className="text-center py-12 text-[12.5px] text-slate-400 border border-dashed border-slate-200 rounded-md">
          No photos yet. Tap "Upload Photos" to add field documentation.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {photos.map((p) => (
            <div key={p.id} className="border border-slate-200 rounded-md overflow-hidden">
              <img src={p.url} alt={p.name} className="w-full h-40 object-cover" />
              <div className="p-2">
                <div className="text-[11.5px] font-semibold text-slate-700 truncate">{p.name}</div>
                {p.caption && <div className="text-[10.5px] text-slate-500 truncate">{p.caption}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
